<template>
  <sp-tooltip
    v-if="showsTooltip"
    :content="description"
    position="bottom"
    popup-spacing="10"
    open-delay="250"
    close-delay="0"
    no-activator-focus
  >
    <sp-button
      tag="a"
      translate="no"
      :variant="variant"
      size="small"
      block
      :data-testid="TestId.slots.slot"
      :aria-label="label"
      :aria-description="description"
      :href="appointmentSlot.booking_url"
      @click="pick"
    >
      <sp-icon v-if="icon" slot="prepend" :name="icon" :size="iconSize"></sp-icon>
      {{ time }}
    </sp-button>
  </sp-tooltip>

  <sp-button
    v-else
    tag="a"
    translate="no"
    :variant="variant"
    size="small"
    block
    :data-testid="TestId.slots.slot"
    :title="description"
    :aria-label="label"
    :href="appointmentSlot.booking_url"
    @click="pick"
  >
    <sp-icon v-if="icon" slot="prepend" :name="icon" :size="iconSize"></sp-icon>
    {{ time }}
  </sp-button>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { formatSlotTime, visitTypeIcon } from "./slot-format";

const props = defineProps({
  /**
   * The slot: `{ start_time, booking_url, visit_type_categories_description, visit_type_categories }`.
   *
   * @type {Object}
   */
  appointmentSlot: {
    type: Object,
    required: true,
  },
  /**
   * `sp-button` variant.
   *
   * @type {String}
   */
  variant: {
    type: String,
    default: "outlined",
  },
  /**
   * Shows the visit type in an `sp-tooltip` rather than the link's native `title`.
   *
   * @type {Boolean}
   */
  tooltip: {
    type: Boolean,
    default: false,
  },
  /**
   * Accessible name. Omit to name the link by its time.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * `sp-icon` size. `null` leaves the size to the `--sp-comp-button-*-icon-size` token.
   *
   * @type {String}
   */
  iconSize: {
    type: String,
    default: "small",
  },
});

const emit = defineEmits(["pick"]);

const icon = computed(() => visitTypeIcon(props.appointmentSlot));
const description = computed(() => props.appointmentSlot.visit_type_categories_description);
const showsTooltip = computed(() => props.tooltip && Boolean(description.value));
const time = computed(() => formatSlotTime(props.appointmentSlot.start_time));

function pick() {
  emit("pick", props.appointmentSlot);
}
</script>

<style scoped lang="scss">
sp-button {
  --sp-comp-button-small-padding: var(--sp-comp-slot-button-padding, 0);
  --sp-comp-button-font-weight: var(--sp-comp-slot-button-font-weight, var(--sp-sys-typescale-body-font-weight));
  --sp-comp-button-primary-bg-color: var(--sp-comp-slot-button-bg-color);
  --sp-comp-button-primary-hover-bg-color: var(--sp-comp-slot-button-hover-bg-color);
}

sp-icon {
  margin-inline-end: var(--sp-comp-slot-button-icon-gap, 0);
}

sp-tooltip {
  --sp-comp-tooltip-light-background: var(--sp-comp-slot-button-tooltip-bg-color);
  --sp-comp-tooltip-light-text-color: var(--sp-comp-slot-button-tooltip-text-color);
  --sp-comp-tooltip-light-shadow-filter: var(--sp-comp-slot-button-tooltip-shadow-filter);
  --sp-comp-tooltip-light-padding: var(--sp-comp-slot-button-tooltip-padding);
  --sp-comp-tooltip-light-font: var(--sp-comp-slot-button-tooltip-font-short);
  --sp-comp-tooltip-light-font-size: var(--sp-comp-slot-button-tooltip-font-size);
  --sp-comp-tooltip-light-border-radius: var(--sp-comp-slot-button-tooltip-border-radius);
  --sp-comp-tooltip-light-border-width: var(--sp-comp-slot-button-tooltip-border-width);
  --sp-comp-tooltip-light-min-height: var(--sp-comp-slot-button-tooltip-min-height);
  --sp-comp-tooltip-light-min-width: var(--sp-comp-slot-button-tooltip-min-width);
  --sp-comp-tooltip-justify-content: var(--sp-comp-slot-button-tooltip-justify-content);

  display: grid;
}
</style>
